<script setup>
import { debounce } from 'lodash';
import { computed, ref, watch, onMounted, useTemplateRef } from 'vue';
import api from './api';
import FacetFilters from './components/FacetFilters.vue';
import SearchResultFullListing from './components/SearchResultFullListing.vue';
import Icon from './components/Icon.vue';

const searchQuery = ref('');
const searchQueryTyped = ref('');
const facetQuery = ref([]);
const facetValue = computed(() => facetQuery.value);
const queryValue = computed((previous) => {
	if (searchQuery.value.length >= 3) return searchQuery.value;

	return previous;
});
const PER_PAGE = 30;

const searchParams = ref({
	query: queryValue,
	perPage: PER_PAGE,
	responseType: 'JsonObject',
	fields: '*',
	postFilters: {
		Category: facetValue
	},
	enableFacetFiltering: true
});
const { data, error, searching: isSearching } = api.search(searchParams);

let autoCompleteParams = ref({
	query: queryValue,
	results: 5
});

const { data: autocompleteData, error: autocompleteError } = api.autocomplete(autoCompleteParams);

const submitBtn = useTemplateRef('btn-submit');

onMounted(() => {
	let searchParams = new URLSearchParams(window.location.search);

	if (!searchParams.get('query')) return;

	searchQueryTyped.value = searchParams.get('query');

	submitBtn.value.click();
});

function onSuggestionClicked(item) {
	searchQuery.value = item;
	searchQueryTyped.value = item;
}

function onSubmit(event) {
	searchQuery.value = searchQueryTyped.value;
}

function onFacetClicked(item) {
	facetQuery.value = [item.value];
}

const resultCount = computed(() => data?.value?.TotalDocument.toLocaleString('en'));

const facets = computed(() => {
	if (!data?.value) return [];

	let items = data?.value?.Facets?.Category?.Items;
	let resultCount = data?.value?.TotalDocument ?? 0;

	items = items
		.filter((item) => item.Count > 0)
		.map((item) => ({
			name: item.Key,
			value: item.Key,
			count: item.Count.toLocaleString('en'),
			key: `${item.Key}.${item.Count}`
		}));

	items.unshift({
		name: 'All Results',
		value: '',
		count: null,
		key: `All Results`
	});

	return items;
});
</script>

<template>
	<div class="search-full-wrap">
		<div class="search-full-wrap__item">
			<div role="search">
				<form class="search-full-form" @submit.prevent="onSubmit">
					<div class="search-full-form__row">
						<div class="search-full-form__input-wrap">
							<input
								class="search-full-form__input"
								type="search"
								v-model="searchQueryTyped"
								name="query"
								placeholder="Search"
							/>
						</div>

						<div class="search-full-form__submit-wrap">
							<button
								class="search-full-form__submit-btn"
								type="submit"
								ref="btn-submit"
							>
								Search
							</button>
						</div>
					</div>
				</form>
			</div>
		</div>

		<div class="search-full-wrap__item">
			<FacetFilters :items="facets" @facet-clicked="onFacetClicked" />
		</div>

		<div class="search-full-wrap__item" v-if="data">
			<p class="search-full-wrap__result-text">
				Your search for
				<strong>
					<em>{{ searchQuery }}</em>
				</strong>
				returned {{ resultCount }} results. Showing the top {{ PER_PAGE }}.
			</p>
		</div>

		<div class="search-full-wrap__item">
			<SearchResultFullListing
				:data="data"
				:is-searching="isSearching"
				:suggestions="autocompleteData"
				@suggestion-clicked="onSuggestionClicked"
			/>
		</div>
	</div>
</template>
